import { useEffect, useRef, useState, type FormEvent } from 'react';
import { HttpError, errText, get, post, setCsrf } from '../../api/client';
import { authBase, config } from '../../api/config';
import { Brand } from '../../ui/brand';

/** What the server says about an invite (GET /portal/invite): who invited, to what, and whether the address has an account. */
interface InviteInfo { email: string; client: { name: string }; from: { name: string }; account: 'new' | 'existing'; role?: 'admin' | 'requester' | 'viewer' }

/** What the invite lets them do, in the words of their role (docs/USERS.md); neutral when the server does not say. */
export const inviteDoes = (i: InviteInfo) =>
  i.role === 'viewer' ? 'to follow requests and the updates on them'
    : i.role === 'admin' ? 'to send requests, follow their progress and manage who can use the portal'
      : i.role === 'requester' ? 'to send requests and follow their progress'
        : `to the ${i.client.name} portal, where you follow requests and their updates`;

export const inviteToken = () => new URLSearchParams(location.hash.replace(/^#/, '')).get('invite') ?? '';
export const isJoinPath = () => location.pathname === '/portal/join';

const input = 'field field-lg';
const primary = 'btn-primary btn-lg w-full';

const WORDS: Record<string, string> = {
  no_invite: 'This invite has already been used, has run out, or was withdrawn. Ask the person who invited you to send a new one.',
  rate_limited: 'Too many tries. Wait a minute and try again.',
};
const say = (e: unknown) => (e instanceof HttpError && WORDS[e.code] ? WORDS[e.code]! : errText(e));

/**
 * The invite link's page: `<site>/portal/join#invite=<token>`. A new address makes its account here (name and password,
 * the link proves the email); an address that already has an account signs in first and then joins as itself.
 * `signedIn`: the app already has a session (a customer or a team account). `onChanged` reloads the app after a sign-in or a join.
 */
export function JoinPage({ signedIn, onChanged, onSignOut }: { signedIn: boolean; onChanged: () => Promise<void>; onSignOut: () => Promise<void> }) {
  const [token] = useState(inviteToken);
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [problem, setProblem] = useState<{ code: string; text: string } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState(false);
  const auto = useRef(false);

  const reload = async () => {
    try {
      const i = await get<InviteInfo>(`/portal/invite?token=${encodeURIComponent(token)}`);
      setInfo(i);
      setExisting(i.account === 'existing');
      setProblem(null);
    } catch (e) {
      setProblem({ code: e instanceof HttpError ? e.code : 'error', text: token ? say(e) : WORDS['no_invite']! });
    }
  };
  useEffect(() => { void reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const done = async (r: { csrfToken?: string | null }) => {
    if (r.csrfToken) setCsrf(r.csrfToken);
    history.replaceState(null, '', '/portal');
    await onChanged();
  };
  const join = async (body: object) => {
    setBusy(true);
    setError(null);
    try {
      await done(await post<{ csrfToken?: string | null }>('/portal/join', { token, ...body }));
    } catch (e) {
      if (e instanceof HttpError && (e.code as string) === 'account_exists') setExisting(true);
      else setError(say(e));
      setBusy(false);
    }
  };
  // Signed in and the address already has an account: join as that account without another tap.
  useEffect(() => {
    if (!signedIn || !info || info.account !== 'existing' || auto.current) return;
    auto.current = true;
    void join({});
  }, [signedIn, info]); // eslint-disable-line react-hooks/exhaustive-deps

  const signInThenJoin = async (e: FormEvent) => {
    e.preventDefault();
    if (!info || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (config.mock) await post('/auth/dev/login', { email: info.email });
      else {
        const r = await fetch(authBase() + '/local/signin', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email: info.email, password }), credentials: 'include' });
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        if (!r.ok) throw new Error(j.error || 'That email and password do not match.');
      }
      await onChanged();
    } catch (x) {
      setError(errText(x));
    } finally {
      setBusy(false);
    }
  };
  const create = (e: FormEvent) => { e.preventDefault(); if (!busy) void join({ name: name.trim(), password }); };

  const from = info?.from.name || 'Someone';
  let body;
  if (problem) {
    body = (
      <div className="grid gap-4" data-testid="join-problem" data-code={problem.code}>
        <h2 className="text-[24px] font-semibold tracking-tight">{problem.code === 'no_invite' ? 'This invite is not valid' : 'We could not open this invite'}</h2>
        <p className="text-[15px] text-dim">{problem.text}</p>
        {problem.code !== 'no_invite' ? <button className={primary} onClick={() => void reload()}>Try again</button> : null}
        {!signedIn ? <a className="btn-line btn-lg w-full" href="/" onClick={(e) => { e.preventDefault(); history.replaceState(null, '', '/'); void onChanged(); }}>Go to sign in</a> : null}
      </div>
    );
  } else if (!info) {
    body = <p className="flex items-center gap-3 text-[15px] text-dim" role="status"><span className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-accent" />Opening your invite…</p>;
  } else if (signedIn && info.account === 'new') {
    // Signed in as somebody, but this address has no account yet: the join would be refused (other_account) because it is not theirs.
    body = (
      <div className="grid gap-4" data-testid="join-other">
        <h2 className="text-[24px] font-semibold tracking-tight">You are signed in as someone else</h2>
        <p className="text-[15px] text-dim">This invite is for <b className="text-ink">{info.email}</b>. Sign out, then open the invite again to create that account.</p>
        <button className={primary} onClick={() => void onSignOut()} data-testid="join-signout">Sign out</button>
      </div>
    );
  } else if (signedIn) {
    body = error ? (
      <div className="grid gap-4" data-testid="join-failed">
        <h2 className="text-[24px] font-semibold tracking-tight">{/other account|another account/i.test(error) ? 'You are signed in as someone else' : 'We could not join you'}</h2>
        <p className="text-[15px] text-dim" role="alert">{error}</p>
        <button className={primary} onClick={() => void onSignOut()} data-testid="join-signout">Sign out</button>
      </div>
    ) : <p className="flex items-center gap-3 text-[15px] text-dim" role="status"><span className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-accent" />Joining {info.client.name}…</p>;
  } else if (existing) {
    body = (
      <form className="grid gap-4" onSubmit={signInThenJoin} data-testid="join-existing">
        <div><h2 className="text-[24px] font-semibold tracking-tight">Sign in to join {info.client.name}</h2><p className="mt-1.5 text-[15px] text-dim">{from} invited you. <b className="text-ink">{info.email}</b> already has an account, so sign in with it and we will add you.</p></div>
        <label className="grid gap-1.5 text-[13px] font-medium text-dim">Email<input className={input + ' bg-panel-2'} value={info.email} readOnly aria-readonly="true" /></label>
        <label className="grid gap-1.5 text-[13px] font-medium text-dim">Password<input className={input} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="join-signin-password" /></label>
        {error ? <p className="text-[14px] text-bad-text" role="alert" data-testid="join-error">{error}</p> : null}
        <button className={primary} disabled={busy || !password} data-testid="join-signin">{busy ? 'Signing in…' : 'Sign in and join'}</button>
      </form>
    );
  } else {
    body = (
      <form className="grid gap-4" onSubmit={create} data-testid="join-new">
        <div><h2 className="text-[24px] font-semibold tracking-tight">Join {info.client.name}</h2><p className="mt-1.5 text-[15px] text-dim">{from} invited you {inviteDoes(info)}. Choose a name and a password for <b className="text-ink">{info.email}</b>.</p></div>
        <label className="grid gap-1.5 text-[13px] font-medium text-dim">Your name<input className={input} autoComplete="name" enterKeyHint="next" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="join-name" /></label>
        <label className="grid gap-1.5 text-[13px] font-medium text-dim">Password
          <span className="relative block"><input className={input + ' w-full pr-20'} type={show ? 'text' : 'password'} autoComplete="new-password" enterKeyHint="go" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 10 characters" data-testid="join-password" />
            <button type="button" className="absolute top-1/2 right-1 min-h-11 -translate-y-1/2 rounded-xl px-3 text-[14px] font-medium text-accent-text" onClick={() => setShow(!show)} aria-pressed={show}>{show ? 'Hide' : 'Show'}</button></span>
        </label>
        {error ? <p className="text-[14px] text-bad-text" role="alert" data-testid="join-error">{error}</p> : null}
        <button className={primary} disabled={busy || !name.trim() || password.length < 10} data-testid="join-create">{busy ? 'One moment…' : 'Create my account'}</button>
        <p className="text-center text-[13px] text-dim">Already have an account with another address? Ask {from} to invite that one.</p>
      </form>
    );
  }
  return (
    <div className="h-full overflow-y-auto bg-bg" style={{ paddingBottom: 'env(safe-area-inset-bottom)', paddingTop: 'env(safe-area-inset-top)' }} data-testid="join-page">
      <div className="mx-auto flex min-h-full w-full max-w-[460px] flex-col px-4 py-6 sm:py-12">
        <div className="mb-6 flex items-center gap-2.5">{config.brandName ? <><Brand size={30} mark /><span className="text-[18px] font-semibold tracking-tight">{config.brandName}</span></> : <Brand size={30} />}</div>
        <section className="w-full rounded-3xl border border-line bg-panel p-5 shadow-2 sm:p-8">{body}</section>
      </div>
    </div>
  );
}
